import { EnrollApClassDTO } from "@/api/dto/classes/EnrollApClassDTO";
import { GrandRatingDTO } from "@/api/dto/GrantRatingDTO";
import { PaginationResultDTO } from "@/api/dto/PaginatedResultsDTO";
import {
  PersonClassesEligibilityDTO,
  PersonClassesGroupedDTO,
} from "@/api/dto/PersonClassesDTO";
import { signoutMapper } from "@/api/mappers/SignoutMapper";
import { Signout } from "@/api/models/Person/Signout";
import { paginationResultsMapper } from "@/api/mappers/PaginatedResultsMapper";
import { personDonationMapper } from "@/api/mappers/PersonDonationMapper";
import { PersonDonation } from "@/api/models/Person/PersonDonation";
import { PaginatedResults } from "@/api/models/PaginatedResults";
import { RatingHistory } from "@/api/models/Person/RatingHistory";
import { ratingsHistoryMapper } from "@/api/mappers/RatingsHistoryMapper";
import { RatingsHistoryDTO } from "@/api/dto/RatingsHistoryDTO";
import { PersonCardCreateDTO } from "@/api/models/PersonCardWrite";
import { useUtility } from "../useUtility";
import { RescindRatingDTO } from "@/api/dto/RescindRatingDTO";
import { AxiosError, AxiosResponse } from "axios";
import { ClassEligibilityTypeEnum } from "@/autogen";
import { PersonDTO } from "@/api/dto/PersonDTO";
import { PersonsTagsDTO } from "@/api/dto/PersonTags";
import { DiscountDataDTO } from "@/api/dto/shop/MembershipDiscountDataDTO";
import { assignedRatingMapper } from "@/api/mappers/AssignedRatingMapper";
import { memberCardMapper } from "@/api/mappers/MemberCardMapper";
import { membershipMapper } from "@/api/mappers/MemberShipMapper";
import { personMapper } from "@/api/mappers/PersonMapper";
import { assignedTagMapper } from "@/api/mappers/TagMapper";
import { Person } from "@/api/models/Person";
import { AssignedRating } from "@/api/models/Person/AssignedRating";
import { MemberCard } from "@/api/models/Person/MemberCard";
import { Membership } from "@/api/models/Person/Membership";
import { AssignedTag } from "@/api/models/Person/Tag";
import { useNotificationStore } from "@/stores/notification";
import { useClient } from "../useClient";
import { EnrollJpClassDTO } from "@/api/dto/classes/EnrollJpClassDTO";
import { UpdatePersonAdminHoldDTO } from "@/api/dto/UpdatePersonAdminHoldDTO";
import { personCommentsMapper } from "@/api/mappers/PersonCommentsMapper";
import { PersonComment } from "@/api/models/Person/PersonComment";
import { SkillAssignment } from "@/api/models/Person/SkillAssignment";
import { UnifiedClassPersonSkillFullDTO } from "@/api/dto/skills/UnifiedClassPersonSkillFullDTO";
import { unifiedClassPersonSkillMapper } from "@/api/mappers/UnifiedClassPersonSkillMapper";
import { UnifiedClassPersonSkill } from "@/api/models/skills/UnifiedClassPersonSkill";
import { EditUnifiedClassPersonSkillDTO } from "@/api/dto/skills/EditUnifiedClassPersonSkillDTO";
import { UnifiedClassPersonSkillsChangesDTO } from "@/api/dto/skills/UnifiedClassPersonSkillsChangesDTO";
import {
  SkillHistoryEntry,
  skillHistoryEntryMapper,
} from "@/api/models/skills/SkillHistoryEntry";
import { PersonsMembershipsDTO } from "@/api/dto/PersonsMembershipsDTO";
import { DonationFullDTOClass } from "@/api/dto/DonationFullDTO";

/**
 * Build an endpoint URL with a fixed base of "/staff/persons/*" for person data
 * @param args Any amount of strings or numbers to be appended to the URL base, separated by slashes
 * @example buildPersonEndpoint('1234', 'test') // will result in "/staff/persons/1234/test"
 */
function buildPersonEndpoint(...args: Array<string | number | null>): string {
  return ["/staff/persons", ...args].filter((token) => token != null).join("/");
}

// Type personAPI as a generic ass object that has generic methods
export interface PersonAPI {
  fetchPersonTags: (personId: number) => Promise<AssignedTag[] | undefined>;
  fetchPersonMemberships: (
    personId: number,
    page?: number | undefined,
    pageSize?: number | undefined,
  ) => Promise<PaginatedResults<Membership> | null>;
  fetchPersonRatings: (
    personId: number,
  ) => Promise<AssignedRating[] | undefined>;
  fetchPersonRatingsHistory: (
    personId: number,
    page?: number | undefined,
    pageSize?: number | undefined,
  ) => Promise<PaginatedResults<RatingHistory> | undefined>;
  fetchPersonCards: (
    personId: number,
    page?: number | undefined,
    pageSize?: number | undefined,
  ) => Promise<PaginatedResults<MemberCard> | undefined>;
  fetchPersonWaivers: (personId: number) => Promise<any>;
  fetchPersonRelationships: (personId: number) => Promise<any>;
  fetchPersonClasses: (
    personId: number,
  ) => Promise<PersonClassesGroupedDTO | undefined>;
  fetchPersonClassesEligibility: (
    personId: number,
  ) => Promise<PersonClassesEligibilityDTO | undefined>;
  fetchPersonSignouts: (
    personId: number,
    page?: number,
    pageSize?: number,
  ) => Promise<PaginationResultDTO | undefined>;
  fetchPersonDonations: (
    personId: number,
    page?: number,
    pageSize?: number,
  ) => Promise<PaginatedResults<DonationFullDTOClass> | undefined>;
  fetchPersonMembershipDiscounts: (
    personId: number,
    membershipTypeId: number,
  ) => Promise<DiscountDataDTO[] | undefined>;
  addPerson: (newPerson: Partial<PersonDTO>) => Promise<Person | undefined>;
  sendPerson: (
    personId: number,
    update: Partial<PersonDTO>,
  ) => Promise<Person | undefined>;
  sendCard: (personId: number, config: PersonCardCreateDTO) => Promise<any>;
  sendCardUpdate: (card: MemberCard) => Promise<any>;
  sendPersonAPEnroll: (
    personId: number,
    enrollment: Partial<EnrollApClassDTO>,
    force?: boolean,
  ) => Promise<any>;
  sendPersonJPEnroll: (
    personId: number,
    enrollment: Partial<EnrollJpClassDTO>,
    force?: boolean,
  ) => Promise<any>;
  sendClassUnenroll: (
    personId: number,
    instanceId: number,
    classTitle: string,
    classType: "apClass" | "jpClass",
  ) => Promise<any>;
  sendPersonGrantRating: (
    personId: number,
    grant: GrandRatingDTO,
  ) => Promise<AssignedRating[] | undefined>;
  sendPersonRescindRating: (
    personId: number,
    rescind: RescindRatingDTO,
  ) => Promise<any>;
  sendPersonAddTags: (
    personId: number,
    tags: number[],
  ) => Promise<AxiosResponse<any, any>[] | undefined>;
  sendPersonRemoveTags: (
    personId: number,
    tags: number[],
  ) => Promise<AxiosResponse<any, any>[] | undefined>;
  sendPersonClassOverride: (
    personId: number,
    typeId: number,
    program: number,
    overrideType: ClassEligibilityTypeEnum,
  ) => Promise<any>;
  mergePersons: (
    mergeIntoPersonId: number,
    mergeFromPersonIds: number[],
  ) => Promise<any>;
  sendAdminHold: (
    personId: number,
    updatePersonAdminHoldDTO: UpdatePersonAdminHoldDTO,
  ) => Promise<string | null>;
  fetchPersonComments: (personId: number) => Promise<PersonComment[]>;
  addPersonComment: (
    personId: number,
    comments: string,
  ) => Promise<PersonComment | null>;
  editPersonComment: (
    personId: number,
    commentId: number,
    comments: string,
  ) => Promise<PersonComment | null>;
  deletePersonComment: (
    personId: number,
    commentId: number,
  ) => Promise<string | null>;
  fetchPersonSkills: (personId: number) => Promise<UnifiedClassPersonSkill[]>;
  sendPersonSkills: (
    personId: number,
    assignment: SkillAssignment,
  ) => Promise<UnifiedClassPersonSkill | undefined>;
  updatePersonSkill: (
    personId: number,
    assignId: number,
    update: EditUnifiedClassPersonSkillDTO,
  ) => Promise<UnifiedClassPersonSkill | undefined>;
  fetchPersonSkillsHistory: (personId: number) => Promise<SkillHistoryEntry[]>;
}

// Cache money
let personAPI: PersonAPI | null = null;

/**
 * Collection of Person fetch/send api functions
 * @todo this could be split into two composables, one for fetch and one for send functions to make it more managable
 */
export const usePersonAPI = (): PersonAPI => {
  if (!personAPI) {
    const client = useClient();
    const notificationStore = useNotificationStore();
    const { evalErrorMessage } = notificationStore;

    const fetchPersonSkills = async (personId: number) => {
      try {
        const { data } = await client.get<UnifiedClassPersonSkillFullDTO[]>(
          buildPersonEndpoint(personId, "skills"),
        );

        return data.map(unifiedClassPersonSkillMapper);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person skills.",
          "error",
        );
      }
      return [];
    };

    const fetchPersonSkillsHistory = async (personId: number) => {
      try {
        const { data } = await client.get<UnifiedClassPersonSkillsChangesDTO[]>(
          buildPersonEndpoint(personId, "skills", "history"),
        );

        return data.map(skillHistoryEntryMapper);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person skills.",
          "error",
        );
      }
      return [];
    };

    const sendPersonSkills = async (
      personId: number,
      assignment: SkillAssignment,
    ) => {
      try {
        const { data } = await client.post(
          buildPersonEndpoint(personId, "skills"),
          assignment.toDTO(personId),
        );
        notificationStore.addSnackbar("Skill Assigned", "success");

        return unifiedClassPersonSkillMapper(data);
      } catch (error: any) {
        console.error(error);
        notificationStore.addSnackbar("Failed to add person skill.", "error");
      }
    };

    const updatePersonSkill = async (
      personId: number,
      assignId: number,
      update: EditUnifiedClassPersonSkillDTO,
    ) => {
      try {
        const { data } = await client.put(
          buildPersonEndpoint(personId, "skills", assignId),
          update,
        );
        notificationStore.addSnackbar("Skill Updated", "success");

        return unifiedClassPersonSkillMapper(data);
      } catch (error: any) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to update person skill.",
          "error",
        );
      }
    };

    const fetchPersonTags = async (
      personId: number,
    ): Promise<AssignedTag[] | undefined> => {
      try {
        const res = await client.get<PersonsTagsDTO[]>(
          buildPersonEndpoint(personId, "tags"),
        );

        return res.data.map(assignedTagMapper);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to fetch tags.", "error");
      }
    };

    const fetchPersonMemberships = async (
      personId: number,
      page: number | undefined = undefined,
      pageSize: number | undefined = undefined,
    ): Promise<PaginatedResults<Membership> | null> => {
      try {
        const res = await client.get<PaginatedResults<PersonsMembershipsDTO>>(
          `/staff/persons/${personId}/memberships`,
          {
            params: {
              page,
              pageSize,
            },
          },
        );

        const records = res.data.records.map(membershipMapper);
        const paginationResults = new PaginatedResults<Membership>({
          records: records,
          totalRecords: res.data.totalRecords,
          limit: res.data.limit,
          offset: res.data.offset,
        });
        return paginationResults;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to fetch memberships.", "error");
      }

      return null;
    };

    const fetchPersonRatings = async (
      personId: number,
    ): Promise<AssignedRating[] | undefined> => {
      try {
        const res = await client.get(buildPersonEndpoint(personId, "ratings"));

        return res.data.map(assignedRatingMapper);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to fetch ratings.", "error");
      }
    };

    const fetchPersonRatingsHistory = async (
      personId: number,
      page: number | undefined = undefined,
      pageSize: number | undefined = undefined,
    ): Promise<PaginatedResults<RatingHistory> | undefined> => {
      try {
        const res = await client.get<PaginatedResults<RatingsHistoryDTO>>(
          buildPersonEndpoint(personId, "ratingsHistory"),
          {
            params: {
              page,
              pageSize,
            },
          },
        );

        const records = res.data.records.map(ratingsHistoryMapper);

        return new PaginatedResults({
          records: records,
          totalRecords: res.data.totalRecords,
          limit: res.data.limit,
          offset: res.data.offset,
        });
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch ratings history.",
          "error",
        );
      }
    };

    const fetchPersonCards = async (
      personId: number,
      page: number | undefined = undefined,
      pageSize: number | undefined = undefined,
    ): Promise<PaginatedResults<MemberCard> | undefined> => {
      try {
        const res = await client.get<PaginatedResults<PersonsCardsDTO>>(
          buildPersonEndpoint(personId, "cards"),
          {
            params: {
              page,
              pageSize,
            },
          },
        );

        const records = res.data.records.map(memberCardMapper);
        return new PaginatedResults<MemberCard>({
          records: records,
          totalRecords: res.data.totalRecords,
          limit: res.data.limit,
          offset: res.data.offset,
        });
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to fetch cards.", "error");
      }
    };

    const fetchPersonWaivers = async (personId: number) => {
      try {
        const res = await client.get(
          buildPersonEndpoint(personId, "damage-waivers"),
        );

        return res.data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch damage-waivers.",
          "error",
        );
      }
    };

    const fetchPersonRelationships = async (personId: number) => {
      try {
        const res = await client.get(
          buildPersonEndpoint(personId, "relationships"),
        );

        return res.data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch relationships.",
          "error",
        );
      }
    };

    const fetchPersonClasses = async (
      personId: number,
    ): Promise<PersonClassesGroupedDTO | undefined> => {
      try {
        const res = await client.get(buildPersonEndpoint(personId, "classes"));
        console.log("fetchPersonClasses", res.data);
        return res.data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person classes.",
          "error",
        );
      }
    };

    const fetchPersonClassesEligibility = async (
      personId: number,
    ): Promise<PersonClassesEligibilityDTO | undefined> => {
      try {
        const res = await client.get(
          buildPersonEndpoint(personId, "classEligibility"),
        );
        console.log("fetchPersonClassesEligibility", res.data);
        return res.data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person class eligibility.",
          "error",
        );
      }
    };

    const fetchPersonSignouts = async (
      personId: number,
      page: number = 1,
      pageSize: number = 10,
    ): Promise<PaginationResultDTO | undefined> => {
      try {
        const { data } = await client.get(
          buildPersonEndpoint(personId, "signouts"),
          {
            params: {
              page: page,
              pageSize: pageSize,
            },
          },
        );

        // Process response and map the resulting records to a the Signout model
        return paginationResultsMapper<Signout>(data, signoutMapper);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person signouts.",
          "error",
        );
      }
    };

    const fetchPersonDonations = async (
      personId: number,
      page: number = 1,
      pageSize: number = 10,
    ) => {
      try {
        const { data } = await client.get(
          buildPersonEndpoint(personId, "donations"),
          {
            params: {
              page,
              pageSize,
            },
          },
        );

        return paginationResultsMapper<DonationFullDTOClass>(
          data,
          personDonationMapper,
        );
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch person donations.",
          "error",
        );
      }
    };

    const fetchPersonDiscounts = async (
      personId: number | null,
      membershipTypeId: number | null,
    ): Promise<DiscountDataDTO[] | undefined> => {
      try {
        const res = await client.get<DiscountDataDTO[]>(
          buildPersonEndpoint("discounts", personId, membershipTypeId),
        );
        return res.data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar(
          "Failed to fetch membership discounts.",
          "error",
        );
      }
    };

    const addPerson = async (newPerson: Partial<PersonDTO>) => {
      try {
        const { data } = await client.post(buildPersonEndpoint(), newPerson);
        return personMapper(data);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to add person.", "error");
      }
    };

    const sendPerson = async (personId: number, update: Partial<PersonDTO>) => {
      try {
        const { data } = await client.put(
          buildPersonEndpoint(personId),
          update,
        );
        notificationStore.addSnackbar("Person updated", "success");
        return personMapper(data);
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to update person.", "error");
      }
    };

    const sendCard = async (personId: number, config: PersonCardCreateDTO) => {
      try {
        console.log("sendCard", config);
        const { data } = await client.post(
          buildPersonEndpoint(personId, "cards"),
          config,
        );

        if (data) {
          return data;
        }
      } catch (error: any) {
        console.error(error);
        notificationStore.evalErrorMessage(error, "Failed to add card.");
      }
    };

    const sendCardUpdate = async (card: MemberCard) => {
      const { unMapPseudoBool } = useUtility();
      try {
        const { data } = await client.put(`/staff/personsCards/${card.id}`, {
          cardNum: String(card.cardNumber),
          active: unMapPseudoBool(card.status),
        });
        notificationStore.addSnackbar("Card updated", "success");
        return data;
      } catch (error) {
        console.log(error);
        notificationStore.evalErrorMessage(error, "Failed to update card");
      }
    };

    const sendPersonAPEnroll = async (
      personId: number,
      enrollment: Partial<EnrollApClassDTO>,
      force: boolean = false,
    ) => {
      try {
        const { data } = await client.post(
          buildPersonEndpoint(
            personId,
            "enroll",
            "apClass",
            force ? "force" : null,
          ),
          enrollment,
        );

        if (data && data.signupType && data.signupType === "W") {
          notificationStore.addSnackbar("Placed on waitlist", "success");
        } else {
          notificationStore.addSnackbar("Class Enrolled", "success");
        }

        return data;
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar("Failed to enroll class", "error");
        if (error?.response?.data?.errorMsg) {
          notificationStore.addSnackbar(error.response.data.errorMsg, "error");
        }
      }
    };

    const sendPersonJPEnroll = async (
      personId: number,
      enrollment: Partial<EnrollJpClassDTO>,
      force: boolean = false,
    ) => {
      try {
        const { data } = await client.post(
          buildPersonEndpoint(
            personId,
            "enroll",
            "jpClass",
            force ? "force" : null,
          ),
          enrollment,
        );

        if (data && data.signupType && data.signupType === "W") {
          notificationStore.addSnackbar("Placed on waitlist", "success");
        } else {
          notificationStore.addSnackbar("Class Enrolled", "success");
        }

        return data;
      } catch (error: any) {
        console.log(error);
        notificationStore.addSnackbar("Failed to enroll class", "error");
        if (error?.response?.data?.errorMsg) {
          notificationStore.addSnackbar(error.response.data.errorMsg, "error");
        }
      }
    };

    const sendClassUnenroll = async (
      personId: number,
      instanceId: number,
      classTitle: string,
      classType: "apClass" | "jpClass",
    ) => {
      try {
        const { data } = await client.post(
          buildPersonEndpoint(personId, "unenroll", classType),
          {
            personId: personId,
            instanceId: instanceId,
            cancelType: "U",
            isOnline: "N",
          },
        );

        notificationStore.addSnackbar(
          `Unenrolled from ${classTitle}`,
          "success",
        );

        return data;
      } catch (error: any) {
        console.log(error);
        // We assume this is an AxiosError and data.error is a string
        if (
          error?.response?.data?.error?.includes(
            "integrity constraint (CBI_DEV2.AP_CLASS_ATTENDANCE_FK2) violated - child record found",
          )
        ) {
          notificationStore.addSnackbar(
            "Cannot unenroll a person whose attendance has been taken.",
            "error",
          );
        } else {
          notificationStore.addSnackbar("Failed to unenroll class", "error");
        }
      }
    };

    const sendPersonGrantRating = async (
      personId: number,
      grant: GrandRatingDTO,
    ): Promise<AssignedRating[] | undefined> => {
      try {
        const { data, status } = await client.post(
          buildPersonEndpoint(personId, "ratings"),
          grant,
        );
        if (status === 200) {
          notificationStore.addSnackbar("Rating granted.", "success");
        }
        return data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to grant rating.", "error");
      }
    };

    const sendPersonRescindRating = async (
      personId: number,
      rescind: RescindRatingDTO,
    ) => {
      try {
        const { data } = await client.delete(
          buildPersonEndpoint(personId, "ratings"),
          { data: rescind },
        );

        if (data) {
          notificationStore.addSnackbar("Rating rescinded.", "success");
        }
        return data;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to rescind rating.", "error");
      }
    };

    const sendPersonAddTags = async (personId: number, tags: number[]) => {
      try {
        const response = await Promise.all(
          tags.map((tagId) =>
            client.post<Promise<Partial<PersonsTagsDTO>>>(
              buildPersonEndpoint(personId, "tags"),
              { tagId },
            ),
          ),
        );

        // console.log("sendPersonAddTags", response);

        return response;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to add tags.", "error");
      }
    };

    const sendPersonRemoveTags = async (personId: number, tags: number[]) => {
      try {
        // const response = await client.post(buildPersonEndpoint(personId, 'tags'), tagId);
        const response = await Promise.all(
          tags.map((assignId) =>
            client.delete<Promise<Partial<PersonsTagsDTO>>>(
              buildPersonEndpoint(personId, "tags"),
              { data: { assignId } },
            ),
          ),
        );

        // console.log("sendPersonRemoveTags", response);

        return response;
      } catch (error) {
        console.error(error);
        notificationStore.addSnackbar("Failed to add tags.", "error");
      }
    };

    const sendPersonClassOverride = async (
      personId: number,
      typeId: number,
      program: number,
      overrideType: ClassEligibilityTypeEnum,
    ) => {
      try {
        const { data, status } = await client.post(
          buildPersonEndpoint(
            personId,
            "typeOverride",
            program === 1 ? "apClass" : "jpClass",
          ),
          {
            typeId,
            overrideType,
          },
        );

        console.log("sendPersonClassOverride", data);
        if (status === 200) {
          notificationStore.addSnackbar(
            "Class type override applied.",
            "success",
          );
        }

        return data;
      } catch (error: any) {
        console.error(error);

        // @todo not a good way to check for this specific error
        // We assume this is an AxiosError and data.error is a string
        if (error?.response?.data?.error?.includes("already exists")) {
          notificationStore.addSnackbar(
            "Failed to add override: Override already exist.",
            "error",
          );
        } else {
          notificationStore.addSnackbar("Failed to add override.", "error");
        }
      }
    };

    const mergePersons = async (
      mergeIntoPersonId: number,
      mergeFromPersonIds: number[],
    ) => {
      try {
        await client.post(buildPersonEndpoint(mergeIntoPersonId, "merge"), {
          mergeFromPersonIds,
        });

        return true;
      } catch (error: any) {
        console.error(error);

        let errMsg = "";
        if (error?.response?.status === 403 && error.response?.data?.error) {
          // unauthorized
          errMsg = error.response.data.error;
        } else {
          // generic error
          errMsg = "An error occured while merging persons.";
        }
        notificationStore.addSnackbar(errMsg, "error");
        return false;
      }
    };

    const sendAdminHold = async (
      personId: number,
      updatePersonAdminHoldDTO: UpdatePersonAdminHoldDTO,
    ) => {
      try {
        const ret = await client.post(
          buildPersonEndpoint(personId, "adminHold"),
          updatePersonAdminHoldDTO,
        );
        return ret.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    };

    const fetchPersonComments = async (
      personId: number,
    ): Promise<PersonComment[]> => {
      try {
        const ret = await client.get(buildPersonEndpoint(personId, "comments"));

        return ret.data.map(personCommentsMapper);
      } catch (error) {
        console.error(error);
        return [];
      }
    };

    const addPersonComment = async (
      personId: number,
      comments: string,
    ): Promise<PersonComment | null> => {
      try {
        const ret = await client.post(
          buildPersonEndpoint(personId, "comments"),
          { comments },
        );

        if (!ret.data) return null;
        return personCommentsMapper(ret.data);
      } catch (error) {
        console.log(error);
        evalErrorMessage(error, "Error creating person comment.");
        return null;
      }
    };

    const editPersonComment = async (
      personId: number,
      commentId: number,
      comments: string,
    ): Promise<PersonComment | null> => {
      try {
        const ret = await client.put(
          buildPersonEndpoint(personId, "comments", commentId),
          { comments },
        );
        return personCommentsMapper(ret.data);
      } catch (error) {
        console.error(error);
        evalErrorMessage(error, "Error editing person comment.");
        return null;
      }
    };

    const deletePersonComment = async (
      personId: number,
      commentId: number,
    ): Promise<string | null> => {
      try {
        const url = buildPersonEndpoint(personId, "comments", commentId);
        const ret = await client.delete(url);
        return ret.data;
      } catch (error) {
        console.error(error);
        evalErrorMessage(error, "Error deleting person comment.");
        return null;
      }
    };

    personAPI = {
      fetchPersonTags,
      fetchPersonMemberships,
      fetchPersonRatings,
      fetchPersonRatingsHistory,
      fetchPersonCards,
      fetchPersonWaivers,
      fetchPersonRelationships,
      fetchPersonClasses,
      fetchPersonClassesEligibility,
      fetchPersonSignouts,
      fetchPersonDonations,
      fetchPersonMembershipDiscounts: fetchPersonDiscounts,
      addPerson,
      sendPerson,
      sendCard,
      sendCardUpdate,
      sendPersonAPEnroll,
      sendPersonJPEnroll,
      sendClassUnenroll,
      sendPersonGrantRating,
      sendPersonRescindRating,
      sendPersonAddTags,
      sendPersonRemoveTags,
      sendPersonClassOverride,
      mergePersons,
      sendAdminHold,
      fetchPersonComments,
      addPersonComment,
      editPersonComment,
      deletePersonComment,
      fetchPersonSkills,
      sendPersonSkills,
      updatePersonSkill,
      fetchPersonSkillsHistory,
    };
  }

  return personAPI;
};
